import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { useStrategyOptions } from '../../../../../utils/hooks';
import KubitModal from '../../../../KubitModal';
import * as FormComponents from '../../../../Form';
import { CommentField, DescriptionField, NameField } from '../../../../Form';
import { PrimaryButton, SecondaryButton } from '../../../../Button';
import {
  IManageVersionEntityMutation,
  IManageVersionEntityMutationVariables,
  INamedBucketDefCreateMutation,
  INamedBucketDefCreateMutationVariables,
  INamedBucketDefEditMutation,
  INamedBucketDefEditMutationVariables,
  INamedBucketDefFieldsFragment,
  ManagedEntityType,
  PermissionKey,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  CREATE_NAMED_BUCKET_DEF,
  EDIT_NAMED_BUCKET_DEF,
} from '../../../../../pages/Formulas/apollo';
import { useAppDispatch } from '../../../../../redux/hooks';
import { closeModal } from '../../../../../redux/slices/modal';
import { IBinning } from '../../../../../redux/slices/oldBuilder/binning/types';
import printGraphqlErrors from '../../../../../utils/apollo/printGraphqlErrors';
import { generateBasicBucketDefInput } from '../../../utils/builderUtils';
import { ModalActions } from '../../../../KubitModal/KubitModalUI';
import NamedBucketDefsMenu from './NamedBucketDefsMenu';
import RadioGroup from '../../../../RadioGroup';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../../pages/PermissionControls/PermissionElementWrapper';
import { ApplyTags } from '../../../../Categories';
import { useSaveCategories } from '../../../../../utils/categories';
import toastMessage from '../../../../../utils/toastMessage';
import { ICreatableReactSelectOption } from '../../../../../utils/types';
import { MANAGE_VERSION_ENTITY } from '../../../../../utils/apollo/manageView/mutations';
import { defaultManagedOption } from '../../../../Categories/utils';
import ThinDivider from '../../../../ThinDivider';
import {
  BUCKET_NAME_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
} from '../../../../../config/constants';

interface IProps {
  selectedBinning: IBinning;
  cachedNamedBucketDef: INamedBucketDefFieldsFragment | null;
  activeSchemaId: string;
  selectedFieldKey: string | null;
  onSubmit: (namedBucketDef: INamedBucketDefFieldsFragment) => void;
}

const CommentFormGroup = styled(CommentField)`
  margin-top: 24px;
`;

const StrategyOptions = styled(RadioGroup)`
  margin-bottom: 24px;
`;
const TagsContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledThinDivider = styled(ThinDivider)`
  margin-top: 20px;
  margin-bottom: 36px;
`;

export default ({
  selectedBinning,
  cachedNamedBucketDef,
  activeSchemaId,
  selectedFieldKey,
  onSubmit,
}: IProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [namedBucketDefId, setNamedBucketDefId] = useState<string | null>(
    cachedNamedBucketDef?.id ?? null,
  );
  const { checkIsPermitted } = usePermission();
  const dispatch = useAppDispatch();
  const isPermittedToCreateBucketDef = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.BucketDefCreate],
    schemaId: activeSchemaId,
  });
  const isPermittedToUpdateBucketDef = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.BucketDefEdit],
    schemaId: activeSchemaId,
  });

  const isPermittedToReadBucketDef = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.BucketDefRead],
    schemaId: activeSchemaId,
  });

  // If user was working with a named bucket - we will use
  // update strategy be default
  const { strategy, setStrategy, strategyOptions } = useStrategyOptions({
    defaultStrategy: isPermittedToCreateBucketDef ? 'create' : 'update',
    permittedStatus: {
      create: isPermittedToCreateBucketDef,
      update: isPermittedToUpdateBucketDef,
    },
  });

  const handleChangeStrategy = (selectedStrategy: string) => {
    setStrategy(selectedStrategy);

    if (name) {
      setName('');
    }

    if (namedBucketDefId) {
      setNamedBucketDefId(null);
    }

    if (description) {
      setDescription('');
    }

    if (comment) {
      setComment('');
    }
  };

  const handleCreateMutationComplete = ({
    namedBucketDefCreate,
  }: INamedBucketDefCreateMutation) => {
    if (printGraphqlErrors(namedBucketDefCreate?.errors)) {
      return;
    }

    const namedBucketDef = namedBucketDefCreate?.entity;

    if (namedBucketDef) {
      toastMessage({
        message: `${namedBucketDef.name} has been created.`,
        type: 'info',
      });

      onSubmit(namedBucketDef);
    }

    if (namedBucketDef && isEntityManaged) {
      handleManagedTag(namedBucketDef.id);
    }

    // close the menu after create and autofill the bucket selector
    closeCurrModal();
  };

  const [createNamedBucket, { loading: namedBucketDefIsLoading }] = useMutation<
    INamedBucketDefCreateMutation,
    INamedBucketDefCreateMutationVariables
  >(CREATE_NAMED_BUCKET_DEF, {
    onCompleted: handleCreateMutationComplete,
  });

  const [manageVersionedEntity] = useMutation<
    IManageVersionEntityMutation,
    IManageVersionEntityMutationVariables
  >(MANAGE_VERSION_ENTITY);

  const handleUpdateMutationComplete = ({
    namedBucketDefEdit,
  }: INamedBucketDefEditMutation) => {
    if (printGraphqlErrors(namedBucketDefEdit?.errors)) {
      return;
    }

    const namedBucketDef = namedBucketDefEdit?.entity;

    if (namedBucketDef) {
      toastMessage({
        message: `${namedBucketDef.name} has been updated.`,
        type: 'info',
      });

      onSubmit(namedBucketDef);
    }

    if (namedBucketDef && isEntityManaged) {
      handleManagedTag(namedBucketDef.id);
    }

    // close the menu after create and autofill the bucket selector
    closeCurrModal();
  };

  const [updateNamedBucket, { loading: namedBucketDefIsUpdating }] =
    useMutation<
      INamedBucketDefEditMutation,
      INamedBucketDefEditMutationVariables
    >(EDIT_NAMED_BUCKET_DEF, {
      onCompleted: handleUpdateMutationComplete,
    });

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(activeSchemaId);

  const handleChangeTags = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const closeCurrModal = () => {
    dispatch(closeModal());
  };

  const handleSelectNamedBucketDef = (
    namedBucketDef: INamedBucketDefFieldsFragment,
  ) => {
    setNamedBucketDefId(namedBucketDef.id);
    setDescription(namedBucketDef?.description ?? '');
    setCategories(
      namedBucketDef?.categories?.map((cat) => ({
        label: cat.name,
        value: cat.id,
      })) ?? [],
    );
    setName(namedBucketDef.name);
    setComment(namedBucketDef?.comment ?? '');

    if (namedBucketDef?.isManaged) {
      setIsEntityManaged(true);
    }
  };

  const handleClearNamedBucketDef = () => {
    setNamedBucketDefId(null);
    setDescription('');
    setCategories([]);
    setComment('');
  };

  const handleManagedTag = async (bucketId: string) => {
    const res = await manageVersionedEntity({
      variables: {
        input: {
          entityId: bucketId,
          entityType: ManagedEntityType.BucketDef,
          isManaged: true,
        },
      },
    });

    if (res.data?.manageVersionEntity?.errors) {
      printGraphqlErrors(res.data.manageVersionEntity.errors);
    }
  };

  const handleCreateNamedBucketDef = async () => {
    const inputCategories = await prepareCategories();

    createNamedBucket({
      variables: {
        input: {
          name,
          description,
          comment,
          basicInput: generateBasicBucketDefInput(
            selectedBinning,
            activeSchemaId,
            selectedFieldKey,
          ),
          categories: inputCategories,
        },
      },
    });
  };

  const handleUpdateNamedBucketDef = async () => {
    if (!namedBucketDefId) {
      return;
    }

    const inputCategories = await prepareCategories();

    await updateNamedBucket({
      variables: {
        input: {
          name: null,
          bucketDefId: namedBucketDefId,
          description,
          comment,
          basicInput: generateBasicBucketDefInput(
            selectedBinning,
            activeSchemaId,
            selectedFieldKey,
          ),
          categories: inputCategories,
        },
      },
    });
  };

  const isSaveDisabled =
    !name ||
    namedBucketDefIsLoading ||
    namedBucketDefIsUpdating ||
    (!namedBucketDefId && !name.length) ||
    name.length > BUCKET_NAME_MAX_LENGTH ||
    comment.length > DESCRIPTION_MAX_LENGTH ||
    description.length > DESCRIPTION_MAX_LENGTH;

  return (
    <KubitModal open={true} onClose={closeCurrModal} header="Save Named Bin">
      <StrategyOptions
        name="strategy"
        value={strategy}
        onChange={handleChangeStrategy}
        options={strategyOptions}
      />
      {strategy === 'create' ? (
        <NameField
          label={'Name'}
          value={name}
          onChange={setName}
          placeholder="Enter new bin name"
          targetErrorElement="Bin"
          maxLength={BUCKET_NAME_MAX_LENGTH}
          disabled={namedBucketDefIsLoading}
        />
      ) : (
        <FormComponents.VerticalField>
          <FormComponents.FieldLabel>Name</FormComponents.FieldLabel>
          <PermissionElementWrapper isPermitted={isPermittedToReadBucketDef}>
            <NamedBucketDefsMenu
              onChange={handleSelectNamedBucketDef}
              onClear={handleClearNamedBucketDef}
              selectedKind={selectedBinning.kind}
              activeSchemaId={activeSchemaId}
              selectedFieldKey={selectedFieldKey}
              cachedNamedBucketDef={
                namedBucketDefId && cachedNamedBucketDef
                  ? cachedNamedBucketDef
                  : null
              }
              isDisabled={!isPermittedToReadBucketDef}
            />
          </PermissionElementWrapper>
        </FormComponents.VerticalField>
      )}
      <DescriptionField
        label={'Description'}
        value={description}
        onChange={setDescription}
        placeholder={'Add bin description here'}
        targetErrorElement="Bin"
        disabled={namedBucketDefIsLoading || namedBucketDefIsUpdating}
      />
      <TagsContainer>
        <ApplyTags
          schemaId={activeSchemaId}
          selectedValues={categories}
          onChange={handleChangeTags}
          isManaged={isEntityManaged}
        />
      </TagsContainer>
      <StyledThinDivider layout="horizontal" size="full" />
      <CommentFormGroup
        label={'Comment'}
        value={comment}
        onChange={setComment}
        targetErrorElement="Bin"
        placeholder="Add your comment here"
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={closeCurrModal}
          disabled={namedBucketDefIsLoading || namedBucketDefIsUpdating}
          trackContext="named binning"
          trackAction="cancel"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={
            strategy === 'create'
              ? handleCreateNamedBucketDef
              : handleUpdateNamedBucketDef
          }
          trackContext="named binning"
          trackAction="save"
          disabled={isSaveDisabled}
        >
          Save
        </PrimaryButton>
      </ModalActions>
    </KubitModal>
  );
};
